import {HANDLE_TTL_FALLBACK_S, MAX_CODES_PER_WINDOW, RESEND_COOLDOWN_S, RESERVATION_CODE_LENGTH} from "../constants/reservation_constants";
import {IReservationError} from "../types/IReservationError";
import {IReservationResendView} from "../types/IReservationResendView";
import {IHandleState, IReservationConfirmAttempt, ReservationFlowState} from "../types/ReservationFlowState";
import {getLockedUntil, isLocked, secondsUntil} from "./reservation_time";

const COMPLETE_CODE_PATTERN = new RegExp(`^\\d{${RESERVATION_CODE_LENGTH}}$`);

// `now` is always epoch milliseconds, `expiresIn` comes in seconds straight from the initiate response,
// `codesSent` - codes the property has got within the CRM's window, this one included (earlier runs of the flow count too)
export type ReservationFlowAction =
    | {type: "START"}
    | {type: "INITIATE_SUCCESS"; handle: string; expiresIn?: number; codesSent: number; now: number}
    | {type: "INITIATE_FAILURE"; error: IReservationError; now: number}
    | {type: "RESEND"; now: number}
    | {type: "RESEND_SUCCESS"; handle: string; expiresIn?: number; codesSent: number; now: number}
    | {type: "RESEND_FAILURE"; error: IReservationError; now: number}
    | {type: "CODE_SUBMIT"; code: string; now: number}
    | {type: "CONFIRM_SUCCESS"; applicationUuid: string | null}
    | {type: "CONFIRM_FAILURE"; error: IReservationError; now: number}
    // `handle` - the handle the expiry timer was set for, so a late timer of an already replaced handle changes nothing
    | {type: "HANDLE_EXPIRED"; handle: string}
    | {type: "RETRY"; now: number}
    | {type: "RESET"};

export type ReservationRetryMode = "confirm" | "initiate";

type CodeEntryState = Extract<ReservationFlowState, {status: "code_entry"}>;
type ConfirmingState = Extract<ReservationFlowState, {status: "confirming"}>;
type FailedState = Extract<ReservationFlowState, {status: "failed"}>;

export const reservationFlowInitialState: ReservationFlowState = {status: "idle"};

const pickHandleState = ({handle, expiresAt, codesSent, resendAvailableAt, lockedUntil}: IHandleState): IHandleState => ({
    handle,
    expiresAt,
    codesSent,
    resendAvailableAt,
    lockedUntil
});

const pickConfirmAttempt = (state: ConfirmingState): IReservationConfirmAttempt => ({...pickHandleState(state), code: state.code});

export const getReservationResendView = (state: ReservationFlowState, now: number): IReservationResendView => {
    if (state.status !== "code_entry") {
        return {state: "blocked", secondsLeft: 0};
    }
    if (state.codesSent >= MAX_CODES_PER_WINDOW) {
        return {state: "exhausted", secondsLeft: 0};
    }

    const lockSecondsLeft = secondsUntil(state.lockedUntil, now);

    if (lockSecondsLeft > 0) {
        return {state: "blocked", secondsLeft: lockSecondsLeft};
    }

    const cooldownSecondsLeft = secondsUntil(state.resendAvailableAt, now);

    if (cooldownSecondsLeft > 0) {
        return {state: "cooldown", secondsLeft: cooldownSecondsLeft};
    }

    return {state: "available", secondsLeft: 0};
};

// null while there is nothing to retry: a terminal failure, or a rate limit that has not lifted yet
export const getReservationRetryMode = (state: ReservationFlowState, now: number): ReservationRetryMode | null => {
    if (state.status !== "failed" || !state.error.retryable || secondsUntil(state.retryAvailableAt, now) > 0) {
        return null;
    }

    const {confirmAttempt} = state;

    return state.error.phase === "confirm" && confirmAttempt !== null && now < confirmAttempt.expiresAt ? "confirm" : "initiate";
};

const getExpiresAt = (now: number, expiresIn: number | undefined) =>
    now + (typeof expiresIn === "number" && Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : HANDLE_TTL_FALLBACK_S) * 1000;

const toFailed = (error: IReservationError, now: number, confirmAttempt: IReservationConfirmAttempt | null = null): FailedState => ({
    status: "failed",
    error,
    retryAvailableAt: error.kind === "rate_limited" ? getLockedUntil(error, now) : null,
    confirmAttempt
});

const toConfirming = (state: IReservationConfirmAttempt): ConfirmingState => ({
    status: "confirming",
    ...pickHandleState(state),
    code: state.code,
    isExpired: false
});

const toCodeEntry = (state: ConfirmingState, error: IReservationError, overrides: Partial<CodeEntryState> = {}): CodeEntryState => ({
    status: "code_entry",
    ...pickHandleState(state),
    error,
    isExpired: state.isExpired,
    isResending: false,
    ...overrides
});

type ActionOf<T extends ReservationFlowAction["type"]> = Extract<ReservationFlowAction, {type: T}>;

const reduceInitiateSuccess = (state: ReservationFlowState, action: ActionOf<"INITIATE_SUCCESS">): ReservationFlowState => {
    if (state.status !== "initiating") {
        return state;
    }

    return {
        status: "code_entry",
        handle: action.handle,
        expiresAt: getExpiresAt(action.now, action.expiresIn),
        codesSent: Math.max(1, action.codesSent),
        // the resend link is active right away, the cooldown starts after the first resend
        resendAvailableAt: null,
        lockedUntil: null,
        error: null,
        isExpired: false,
        isResending: false
    };
};

const reduceResend = (state: ReservationFlowState, action: ActionOf<"RESEND">): ReservationFlowState => {
    if (state.status !== "code_entry" || state.isResending || getReservationResendView(state, action.now).state !== "available") {
        return state;
    }

    // the current handle stays until a new one is issued
    return {...state, isResending: true};
};

const reduceResendSuccess = (state: ReservationFlowState, action: ActionOf<"RESEND_SUCCESS">): ReservationFlowState => {
    if (state.status !== "code_entry" || !state.isResending) {
        return state;
    }

    return {
        ...state,
        handle: action.handle,
        expiresAt: getExpiresAt(action.now, action.expiresIn),
        codesSent: Math.max(state.codesSent + 1, action.codesSent),
        resendAvailableAt: action.now + RESEND_COOLDOWN_S * 1000,
        lockedUntil: null,
        error: null,
        isExpired: false,
        isResending: false
    };
};

const reduceResendFailure = (state: ReservationFlowState, action: ActionOf<"RESEND_FAILURE">): ReservationFlowState => {
    if (state.status !== "code_entry" || !state.isResending) {
        return state;
    }

    if (action.error.kind === "rate_limited") {
        return {...state, error: action.error, isResending: false, lockedUntil: getLockedUntil(action.error, action.now)};
    }

    // e.g. a taken property - neither a new code nor the one in the mailbox can be confirmed any more
    if (!action.error.resendAllowed) {
        return toFailed(action.error, action.now);
    }

    // the previous handle and the code already in the mailbox are still valid
    return {...state, error: action.error, isResending: false};
};

const reduceCodeSubmit = (state: ReservationFlowState, action: ActionOf<"CODE_SUBMIT">): ReservationFlowState => {
    if (state.status !== "code_entry" || state.isExpired || state.isResending || isLocked(state, action.now) || !COMPLETE_CODE_PATTERN.test(action.code)) {
        return state;
    }
    // the expiry timer can lag behind, e.g. in a throttled background tab
    if (action.now >= state.expiresAt) {
        return {...state, isExpired: true};
    }

    return toConfirming({...state, code: action.code});
};

const reduceConfirmFailure = (state: ReservationFlowState, action: ActionOf<"CONFIRM_FAILURE">): ReservationFlowState => {
    if (state.status !== "confirming") {
        return state;
    }

    switch (action.error.kind) {
        case "invalid_code":
            return toCodeEntry(state, action.error);
        case "expired":
            return toCodeEntry(state, action.error, {isExpired: true});
        case "rate_limited":
            return toCodeEntry(state, action.error, {lockedUntil: getLockedUntil(action.error, action.now)});
        default:
            return toFailed(action.error, action.now, pickConfirmAttempt(state));
    }
};

const reduceHandleExpired = (state: ReservationFlowState, action: ActionOf<"HANDLE_EXPIRED">): ReservationFlowState => {
    if ((state.status !== "code_entry" && state.status !== "confirming") || state.handle !== action.handle || state.isExpired) {
        return state;
    }

    // an earlier error is superseded - otherwise a failed resend from before would replace the expiry copy
    return state.status === "code_entry" ? {...state, isExpired: true, error: null} : {...state, isExpired: true};
};

const reduceRetry = (state: ReservationFlowState, action: ActionOf<"RETRY">): ReservationFlowState => {
    const mode = getReservationRetryMode(state, action.now);

    if (mode === "confirm" && state.status === "failed" && state.confirmAttempt !== null) {
        return toConfirming(state.confirmAttempt);
    }

    return mode === "initiate" ? {status: "initiating"} : state;
};

export const reservationFlowReducer = (state: ReservationFlowState, action: ReservationFlowAction): ReservationFlowState => {
    switch (action.type) {
        case "START":
            // idempotent - coming back to the code step must not burn another code
            return state.status === "idle" ? {status: "initiating"} : state;
        case "INITIATE_SUCCESS":
            return reduceInitiateSuccess(state, action);
        case "INITIATE_FAILURE":
            return state.status === "initiating" ? toFailed(action.error, action.now) : state;
        case "RESEND":
            return reduceResend(state, action);
        case "RESEND_SUCCESS":
            return reduceResendSuccess(state, action);
        case "RESEND_FAILURE":
            return reduceResendFailure(state, action);
        case "CODE_SUBMIT":
            return reduceCodeSubmit(state, action);
        case "CONFIRM_SUCCESS":
            return state.status === "confirming" ? {status: "confirmed", applicationUuid: action.applicationUuid} : state;
        case "CONFIRM_FAILURE":
            return reduceConfirmFailure(state, action);
        case "HANDLE_EXPIRED":
            return reduceHandleExpired(state, action);
        case "RETRY":
            return reduceRetry(state, action);
        case "RESET":
            return state.status === "idle" ? state : reservationFlowInitialState;
        default:
            return state;
    }
};
import {HANDLE_TTL_FALLBACK_S, MAX_CODES_PER_WINDOW, RESEND_COOLDOWN_S, RESERVATION_CODE_LENGTH} from "../constants/reservation_constants";
import {IReservationError} from "../types/IReservationError";
import {IReservationResendView} from "../types/IReservationResendView";
import {IHandleState, IReservationConfirmAttempt, ReservationFlowState} from "../types/ReservationFlowState";
import {getLockedUntil, isLocked, secondsUntil} from "./reservation_time";

const COMPLETE_CODE_PATTERN = new RegExp(`^\\d{${RESERVATION_CODE_LENGTH}}$`);

// `now` is always epoch milliseconds, `expiresIn` comes in seconds straight from the initiate response,
// `codesSent` - codes the property has got within the CRM's window, this one included (earlier runs of the flow count too)
export type ReservationFlowAction =
    | {type: "START"}
    | {type: "INITIATE_SUCCESS"; handle: string; expiresIn?: number; codesSent: number; now: number}
    | {type: "INITIATE_FAILURE"; error: IReservationError; now: number}
    | {type: "RESEND"; now: number}
    | {type: "RESEND_SUCCESS"; handle: string; expiresIn?: number; codesSent: number; now: number}
    | {type: "RESEND_FAILURE"; error: IReservationError; now: number}
    | {type: "CODE_SUBMIT"; code: string; now: number}
    | {type: "CONFIRM_SUCCESS"; applicationUuid: string | null}
    | {type: "CONFIRM_FAILURE"; error: IReservationError; now: number}
    // `handle` - the handle the expiry timer was set for, so a late timer of an already replaced handle changes nothing
    | {type: "HANDLE_EXPIRED"; handle: string}
    | {type: "RETRY"; now: number}
    | {type: "RESET"};

export type ReservationRetryMode = "confirm" | "initiate";

type CodeEntryState = Extract<ReservationFlowState, {status: "code_entry"}>;
type ConfirmingState = Extract<ReservationFlowState, {status: "confirming"}>;
type FailedState = Extract<ReservationFlowState, {status: "failed"}>;

export const reservationFlowInitialState: ReservationFlowState = {status: "idle"};

const pickHandleState = ({handle, expiresAt, codesSent, resendAvailableAt, lockedUntil}: IHandleState): IHandleState => ({
    handle,
    expiresAt,
    codesSent,
    resendAvailableAt,
    lockedUntil
});

const pickConfirmAttempt = (state: ConfirmingState): IReservationConfirmAttempt => ({...pickHandleState(state), code: state.code});

export const getReservationResendView = (state: ReservationFlowState, now: number): IReservationResendView => {
    if (state.status !== "code_entry") {
        return {state: "blocked", secondsLeft: 0};
    }
    if (state.codesSent >= MAX_CODES_PER_WINDOW) {
        return {state: "exhausted", secondsLeft: 0};
    }

    const lockSecondsLeft = secondsUntil(state.lockedUntil, now);

    if (lockSecondsLeft > 0) {
        return {state: "blocked", secondsLeft: lockSecondsLeft};
    }

    const cooldownSecondsLeft = secondsUntil(state.resendAvailableAt, now);

    if (cooldownSecondsLeft > 0) {
        return {state: "cooldown", secondsLeft: cooldownSecondsLeft};
    }

    return {state: "available", secondsLeft: 0};
};

// null while there is nothing to retry: a terminal failure, or a rate limit that has not lifted yet
export const getReservationRetryMode = (state: ReservationFlowState, now: number): ReservationRetryMode | null => {
    if (state.status !== "failed" || !state.error.retryable || secondsUntil(state.retryAvailableAt, now) > 0) {
        return null;
    }

    const {confirmAttempt} = state;

    return state.error.phase === "confirm" && confirmAttempt !== null && now < confirmAttempt.expiresAt ? "confirm" : "initiate";
};

const getExpiresAt = (now: number, expiresIn: number | undefined) =>
    now + (typeof expiresIn === "number" && Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : HANDLE_TTL_FALLBACK_S) * 1000;

const toFailed = (error: IReservationError, now: number, confirmAttempt: IReservationConfirmAttempt | null = null): FailedState => ({
    status: "failed",
    error,
    retryAvailableAt: error.kind === "rate_limited" ? getLockedUntil(error, now) : null,
    confirmAttempt
});

const toConfirming = (state: IReservationConfirmAttempt): ConfirmingState => ({
    status: "confirming",
    ...pickHandleState(state),
    code: state.code,
    isExpired: false
});

const toCodeEntry = (state: ConfirmingState, error: IReservationError, overrides: Partial<CodeEntryState> = {}): CodeEntryState => ({
    status: "code_entry",
    ...pickHandleState(state),
    error,
    isExpired: state.isExpired,
    isResending: false,
    ...overrides
});

type ActionOf<T extends ReservationFlowAction["type"]> = Extract<ReservationFlowAction, {type: T}>;

const reduceInitiateSuccess = (state: ReservationFlowState, action: ActionOf<"INITIATE_SUCCESS">): ReservationFlowState => {
    if (state.status !== "initiating") {
        return state;
    }

    return {
        status: "code_entry",
        handle: action.handle,
        expiresAt: getExpiresAt(action.now, action.expiresIn),
        codesSent: Math.max(1, action.codesSent),
        // the resend link is active right away, the cooldown starts after the first resend
        resendAvailableAt: null,
        lockedUntil: null,
        error: null,
        isExpired: false,
        isResending: false
    };
};

const reduceResend = (state: ReservationFlowState, action: ActionOf<"RESEND">): ReservationFlowState => {
    if (state.status !== "code_entry" || state.isResending || getReservationResendView(state, action.now).state !== "available") {
        return state;
    }

    // the current handle stays until a new one is issued
    return {...state, isResending: true};
};

const reduceResendSuccess = (state: ReservationFlowState, action: ActionOf<"RESEND_SUCCESS">): ReservationFlowState => {
    if (state.status !== "code_entry" || !state.isResending) {
        return state;
    }

    return {
        ...state,
        handle: action.handle,
        expiresAt: getExpiresAt(action.now, action.expiresIn),
        codesSent: Math.max(state.codesSent + 1, action.codesSent),
        resendAvailableAt: action.now + RESEND_COOLDOWN_S * 1000,
        lockedUntil: null,
        error: null,
        isExpired: false,
        isResending: false
    };
};

const reduceResendFailure = (state: ReservationFlowState, action: ActionOf<"RESEND_FAILURE">): ReservationFlowState => {
    if (state.status !== "code_entry" || !state.isResending) {
        return state;
    }

    if (action.error.kind === "rate_limited") {
        return {...state, error: action.error, isResending: false, lockedUntil: getLockedUntil(action.error, action.now)};
    }

    // e.g. a taken property - neither a new code nor the one in the mailbox can be confirmed any more
    if (!action.error.resendAllowed) {
        return toFailed(action.error, action.now);
    }

    // the previous handle and the code already in the mailbox are still valid
    return {...state, error: action.error, isResending: false};
};

const reduceCodeSubmit = (state: ReservationFlowState, action: ActionOf<"CODE_SUBMIT">): ReservationFlowState => {
    if (state.status !== "code_entry" || state.isExpired || state.isResending || isLocked(state, action.now) || !COMPLETE_CODE_PATTERN.test(action.code)) {
        return state;
    }
    // the expiry timer can lag behind, e.g. in a throttled background tab
    if (action.now >= state.expiresAt) {
        return {...state, isExpired: true};
    }

    return toConfirming({...state, code: action.code});
};

const reduceConfirmFailure = (state: ReservationFlowState, action: ActionOf<"CONFIRM_FAILURE">): ReservationFlowState => {
    if (state.status !== "confirming") {
        return state;
    }

    switch (action.error.kind) {
        case "invalid_code":
            return toCodeEntry(state, action.error);
        case "expired":
            return toCodeEntry(state, action.error, {isExpired: true});
        case "rate_limited":
            return toCodeEntry(state, action.error, {lockedUntil: getLockedUntil(action.error, action.now)});
        default:
            return toFailed(action.error, action.now, pickConfirmAttempt(state));
    }
};

const reduceHandleExpired = (state: ReservationFlowState, action: ActionOf<"HANDLE_EXPIRED">): ReservationFlowState => {
    if ((state.status !== "code_entry" && state.status !== "confirming") || state.handle !== action.handle || state.isExpired) {
        return state;
    }

    // an earlier error is superseded - otherwise a failed resend from before would replace the expiry copy
    return state.status === "code_entry" ? {...state, isExpired: true, error: null} : {...state, isExpired: true};
};

const reduceRetry = (state: ReservationFlowState, action: ActionOf<"RETRY">): ReservationFlowState => {
    const mode = getReservationRetryMode(state, action.now);

    if (mode === "confirm" && state.status === "failed" && state.confirmAttempt !== null) {
        return toConfirming(state.confirmAttempt);
    }

    return mode === "initiate" ? {status: "initiating"} : state;
};

export const reservationFlowReducer = (state: ReservationFlowState, action: ReservationFlowAction): ReservationFlowState => {
    switch (action.type) {
        case "START":
            // idempotent - coming back to the code step must not burn another code
            return state.status === "idle" ? {status: "initiating"} : state;
        case "INITIATE_SUCCESS":
            return reduceInitiateSuccess(state, action);
        case "INITIATE_FAILURE":
            return state.status === "initiating" ? toFailed(action.error, action.now) : state;
        case "RESEND":
            return reduceResend(state, action);
        case "RESEND_SUCCESS":
            return reduceResendSuccess(state, action);
        case "RESEND_FAILURE":
            return reduceResendFailure(state, action);
        case "CODE_SUBMIT":
            return reduceCodeSubmit(state, action);
        case "CONFIRM_SUCCESS":
            return state.status === "confirming" ? {status: "confirmed", applicationUuid: action.applicationUuid} : state;
        case "CONFIRM_FAILURE":
            return reduceConfirmFailure(state, action);
        case "HANDLE_EXPIRED":
            return reduceHandleExpired(state, action);
        case "RETRY":
            return reduceRetry(state, action);
        case "RESET":
            return state.status === "idle" ? state : reservationFlowInitialState;
        default:
            return state;
    }
};
